import { useState } from 'react';

import { useGlobalFiltersHook } from 'hooks/useGlobalFiltersHook';

import { useQueryMilestones } from 'api/queries/getMilestones.query';
import { useQueryGetProjectVersionsList } from 'api/queries/getProjectVersionsList.query';

import { FilterNames, getFiltersData } from 'components/common/GlobalFiltersInPage/globalFiltersInPage.utils';

export const useMilestonesDistributionProvider = ({
    widgetId,
    globalFilterData,
    localFilters,
    contractId,
    latestVersionId,
    projectId,
    endpoints,
}) => {
    const [wbsRangeFilter, setWbsRangeFilter] = useState<number[]>([1, 10]);
    const isInPinboard = Boolean(widgetId);
    const source = isInPinboard ? localFilters : { ...globalFilterData, wbsRangeFilter };

    return {
        ui: {
            state: getState({ source }),
            methods: {
                setWbsRangeFilter: setWbsRangeFilter,
            },
            pills: useGetPills(source, widgetId, latestVersionId),
        },
        api: {
            getProjectVersionsList: useQueryGetProjectVersionsList({ projectId, contractId }),
            getMilestones: {
                ...useQueryMilestones(getMilestonesPayload({ latestVersionId, source }), endpoints.getMilestone),
                endpoint: endpoints.getMilestone,
            },
        },
    };
};

const useGetPills = (source, widgetId, latestVersionId) => {
    const pillsData = {
        activityCodes: source?.activityCodes?.filter((item) => !item.isExclude).map((i) => i.name),
        activityCodesExclude: source?.activityCodes?.filter((item) => item.isExclude).map((i) => i.name),
        milestoneFragnet: source?.milestoneFragnet,
        tags: source?.tags?.filter((item) => !item.isExclude).map((i) => i.name),
        tagsExclude: source?.tags?.filter((item) => item.isExclude).map((i) => i.name),
        wbs: source?.wbs,
        minWbsLevel: source?.wbsRangeFilter[0],
        maxWbsLevel: source?.wbsRangeFilter[1],
    };

    const { pillsList: pills } = useGlobalFiltersHook({
        latestVersionId,
        source: pillsData,
    });

    return pills;
};

const getMilestonesPayload = ({ latestVersionId, source }) => {
    return {
        id: latestVersionId ? [latestVersionId] : [],
        filter: {
            filters: getFiltersData({
                [FilterNames.ACTIVITY_CODES]: source?.activityCodes
                    ?.filter((item) => !item.isExclude)
                    .map((i) => i.name),
                [FilterNames.ACTIVITY_CODES_EXCLUDE]: source?.activityCodes
                    ?.filter((item) => item.isExclude)
                    .map((i) => i.name),
                [FilterNames.FRAGNET_SEARCH]: source?.milestoneFragnet,
                [FilterNames.TAG_CARD_BOARD]: source?.tags?.filter((item) => !item.isExclude).map((i) => i.name),
                [FilterNames.TAG_CARD_BOARD_EXCLUDE]: source?.tags?.filter((item) => item.isExclude).map((i) => i.name),
                [FilterNames.WBS_SEARCH]: source?.wbs,
                [FilterNames.MIN_WBS_LEVEL]: [source?.wbsRangeFilter[0]],
                [FilterNames.MAX_WBS_LEVEL]: [source?.wbsRangeFilter[1]],
            }),
        },
    };
};

const getState = ({ source }) => {
    return {
        // global filters
        activityCodes: source?.activityCodes,
        milestoneFragnet: source?.milestoneFragnet,
        tags: source?.tags,
        wbs: source?.wbs,
        // local filters
        wbsRangeFilter: source?.wbsRangeFilter,
    };
};
